import i18n from "../i18n";

/**
 * A scenario's verdict, as the run page and the scenario browser both show it.
 * Moved out of RunDetail.tsx unchanged on 2026-09-10 so the two pages cannot
 * drift - the very thing verdictOf's own comment is about.
 */

/**
 * What the suite made of a scenario, with the check record as the fallback.
 *
 * MODULE LEVEL AND SHARED, because the two places that needed it drifted. The
 * page grouped with `s.verdict || …` and the group tally counted with
 * `s.verdict ?? …`, and the API sends `""` - not null - for a run whose
 * junit.xml has not arrived. `??` does not catch the empty string, so on such a
 * run every tally came out zero: no counts on any group header, and
 * `shut = collapsed[g] ?? bad === 0` then started EVERY group collapsed.
 * run110 is 159 failures out of 159 scenarios and opened with all of them
 * folded away - the exact opposite of the rule three lines below it.
 *
 * Falling back to failed_checks is deliberate but only until junit arrives:
 * grouping by it would call a draft's expected failure a failure - run45 has 17
 * scenarios with a failed check and 3 real failures, and showing 17 would
 * invent fourteen findings.
 */
export function verdictOf(s: {
  verdict?: string | null;
  failed_checks: number;
}): string {
  return s.verdict || (s.failed_checks > 0 ? "failed" : "passed");
}

/** The verdict as a colour, with a word for anyone the colour does not reach. */
export function Dot({ verdict, label = true }: { verdict: string; label?: boolean }) {
  const tone =
    verdict === "failed" || verdict === "error"
      ? "bg-red-500"
      : verdict === "xfailed"
        ? "bg-amber-400"
        : verdict === "skipped"
          ? "bg-slate-300 dark:bg-slate-600"
          : "bg-success-500";
  return (
    <span className="flex shrink-0 items-center">
      <span className={`h-2 w-2 rounded-full ${tone}`} aria-hidden="true" />
      {label && <span className="sr-only">{verdictWord(verdict)}</span>}
    </span>
  );
}

/** A verdict as people say it: "expected failure", "not run here" - never "xfailed". */
export function verdictWord(verdict: string): string {
  return i18n.t(`verdict.legend.${bucketOf(verdict)}` as "verdict.legend.passed");
}

/** The four lists a run page sorts its cases into. */
export type Bucket = "failed" | "xfailed" | "passed" | "skipped";

/**
 * Which list a verdict belongs to - exactly one, always.
 *
 * The harness uploads five executed verdicts and "skipped"; the page had a
 * list for four of them and counted by name. `error` (the case broke before
 * reaching a verdict) was LISTED with the failures but not COUNTED with them,
 * so a run with one of each read "Failures 1" over two rows - found by sncf-93
 * on 2026-09-11 against a real server response. `xpassed` (it passed, though
 * marked as expected to fail) was counted in the total and listed nowhere.
 *
 * A verdict this page has never heard of goes with the failures: shown where
 * somebody will look, rather than filed among the passes.
 */
export function bucketOf(verdict: string): Bucket {
  switch (verdict) {
    case "passed":
    case "xpassed":
      return "passed";
    case "xfailed":
      return "xfailed";
    case "skipped":
      return "skipped";
    default:
      return "failed";
  }
}

/**
 * How many of a run's cases fall in `bucket`.
 *
 * The verdict table for every case junit reported, because `scenarios` lists
 * only cases that left steps (run114: 273 verdicts, 211 listed). PLUS the
 * listed cases junit has not reported - a run cut off mid-way, or one whose
 * junit.xml has not arrived - by their steps, as verdictOf reads them. The two
 * never overlap: a listed case's `verdict` IS its row in that same table.
 */
export function countIn(
  verdicts: Record<string, number>,
  scenarios: { verdict?: string | null; failed_checks: number }[],
  bucket: Bucket,
): number {
  let n = 0;
  for (const [k, c] of Object.entries(verdicts)) if (bucketOf(k) === bucket) n += c;
  for (const s of scenarios) if (!s.verdict && bucketOf(verdictOf(s)) === bucket) n++;
  return n;
}
